/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './collapsible-sidebar.html';
import {OBJECT_TYPE} from '../constants/object-type';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {AppShellStateService} from '../../base/app-shell-state.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../angular-platform-providers';

const USER_MENUS = ['app.adm.users', 'app.adm.user-profile', 'app.adm.team-groups', 'app.adm.teams', 'app.adm.roles', 'app.adm.notification-preferences', 'app.adm.api-keys'];
const DESIGN_MENUS = ['app.adm.layouts', 'app.adm.layout-includes', 'app.adm.layout-examples', 'app.adm.marketplace-products', 'app.adm.products', 'app.adm.coupons', 'app.adm.email-translations', 'app.adm.email-templates'];
const QUERY_MENUS = ['app.rep.queries', 'app.data-queries'];
const DATA_EXPORT_ADMIN_MENUS = ['app.adm.data-exports'];

const DEFAULT_MENU_VIEW: Record<string, boolean> = {
  dashboards: false,
  advertising: false,
  political: false,
  inventory: false,
  reports: false,
  queries: false,
  users: false,
  design: false,
  admin: false,
  segmentation: false,
  fields: false
};

@Component({
  standalone: false,
  selector: 'adn-collapsible-sidebar',
  template
})
export class CollapsibleSidebarComponent implements OnInit, OnDestroy {
  readonly OBJECT_TYPE = OBJECT_TYPE;

  perms: any = {};
  anyPerms: any = {};
  sectionPerms: any = {};
  menuView: Record<string, boolean> = {...DEFAULT_MENU_VIEW};
  menuViewFromPage: Record<string, boolean> = {...DEFAULT_MENU_VIEW};

  app: string;
  experimental = false;
  suiPlatform = false;
  marketplacePlatform = false;
  pureMarketplacePlatform = false;
  showProducts = false;
  showEmailTranslationsExtra = false;
  showFolios = false;
  showMarketplaceReviews = false;
  showPolitical = false;
  showReleaseNotesAdmin = false;

  private deregisterTransitions: () => void;

  constructor(
    readonly shell: AppShellStateService,
    private readonly localUserPermissions: LocalUserPermissions,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly uiRouter: UIRouter,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly $state: StateService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any
  ) {
  }

  ngOnInit(): void {
    this.experimental = this.localNetworkProfile.isExperimental()
      || this.localUserProfile.get('username') === 'office-mel@adnuntius.com'
      || this.location.hostname === 'localhost';
    this.suiPlatform = this.localNetworkProfile.isSuiPlatform();
    this.marketplacePlatform = this.localNetworkProfile.isMarketplacePlatform();
    this.pureMarketplacePlatform = this.localNetworkProfile.isPureMarketplacePlatform();
    this.showProducts = LocalNetworkInfo.obtain().hasProductFeedProducts();
    this.showFolios = LocalNetworkInfo.obtain().getInfo('creativeSetCount') + LocalNetworkInfo.obtain().getInfo('folioCount') > 0;
    this.showMarketplaceReviews = LocalNetworkInfo.obtain().getInfo('axProductsWithReviewCount');
    this.showPolitical = !!this.localNetworkProfile.get('politicalApp');
    this.showReleaseNotesAdmin = ['adnuntius@adnuntius.com', 'broker1@adnuntius.com'].indexOf(this.localUserProfile.get('username')) > -1;

    this.selectMenu();
    this.deregisterTransitions = this.uiRouter.transitionService.onSuccess({}, () => this.selectMenu()) as () => void;
  }

  ngOnDestroy(): void {
    if (this.deregisterTransitions) {
      this.deregisterTransitions();
    }
  }

  sectionClicked(menuViewParam: string): void {
    this.menuView[menuViewParam] = !this.menuView[menuViewParam];
    if (!this.menuView[menuViewParam]) {
      return;
    }
    _.forEach(this.menuView, (_value, key) => {
      if (menuViewParam !== key && !this.menuViewFromPage[key]) {
        this.menuView[key] = false;
      }
    });
  }

  trackWorkspace(_index: number, workspace: any): any {
    return workspace.id;
  }

  private selectMenu(): void {
    this.app = this.localUserProfile.getCurrentApp();
    this.perms = this.localUserPermissions.getAllNetworkPermissions();
    this.anyPerms = this.localUserPermissions.getAnyVisibility();
    this.sectionPerms = this.localUserPermissions.getSectionPerms(this.app);
    this.menuView = {...DEFAULT_MENU_VIEW};

    const urlState = _.get(this.$state.current, 'name') || '';
    if (this.app === 'DATA') {
      if (this.$state.includes('app.dat-seg')) {
        this.menuView.segmentation = true;
      } else if (this.$state.includes('app.dat-map.fields') || this.$state.includes('app.dat-map.mappings')) {
        this.menuView.fields = true;
      } else if (this.$state.includes('app.data-queries')) {
        this.menuView.queries = true;
      }
    }
    if (this.app === 'POLITICAL'
      && (this.$state.includes('app.pol.political-entities')
        || this.$state.includes('app.pol.political-campaigns')
        || this.$state.includes('app.welcome-political'))) {
      this.menuView.political = true;
    }
    if (this.app === 'ADVERTISING') {
      if (this.$state.includes('app.adv')
        || urlState.indexOf('app.reach-analysis') > -1
        || urlState.indexOf('app.booking-calendar') > -1
        || urlState.indexOf('app.adv.third-party-audiences') > -1) {
        this.menuView.advertising = true;
      } else if (this.$state.includes('app.inv') || urlState.indexOf('app.ad-tag') > -1) {
        this.menuView.inventory = true;
      } else if (this.$state.includes('app.wor') || urlState.indexOf('app.wor') > -1) {
        this.menuView.dashboards = true;
      }
    }
    if (this.$state.includes('app.rep')) {
      this.menuView[QUERY_MENUS.some((menu) => urlState.indexOf(menu) > -1) ? 'queries' : 'reports'] = true;
    } else if (this.$state.includes('app.adm')
      || this.$state.includes('app.simple.release-notes')
      || this.$state.includes('app.simple.release-notes.release-note')) {
      if (USER_MENUS.some((menu) => urlState.indexOf(menu) > -1)) {
        this.menuView.users = true;
      } else if (DESIGN_MENUS.some((menu) => urlState.indexOf(menu) > -1)) {
        this.menuView.design = true;
      } else if (DATA_EXPORT_ADMIN_MENUS.some((menu) => urlState.indexOf(menu) > -1)
        && (this.perms.AX_ADVERTISER_ADMIN || this.perms.AX_PUBLISHER_ADMIN)) {
        this.menuView.queries = true;
      } else {
        this.menuView.admin = true;
      }
    }

    this.menuViewFromPage = {...this.menuView};
    this.showEmailTranslationsExtra = LocalNetworkInfo.obtain().getInfo('emailTranslationsCount') > 0
      && (this.anyPerms.AX_PUBLISHER_TEAM_ADMIN
        || this.anyPerms.AX_PUBLISHER_ADMIN
        || this.anyPerms.AX_ADVERTISER_ADMIN)
      && !this.anyPerms.MANAGE_SYSTEM;
  }
}
